import { FC, useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import { AppFormattedMessage } from '../../../components/app-formatted-message/index';
import Button from '../../../components/button/index';
import { Checkbox } from '../../../components/checkbox/index';
import { CrossfadePanel } from '../../../components/motion/index';
import { StringKey } from '../../../lang/index';
import { capTableStore } from '../../../storage/local/index';
import {
  CapTableOrdering,
  ColumnVariant,
  detailsColumns,
  DetailsSortVariant,
  ViewBy,
} from '../../../types/snapshot.types';
import { StakeholderType } from '../../../types/stakeholder.types';
import {
  CAP_TABLE_ROW_EXPAND_EASING,
  CAP_TABLE_ROW_EXPAND_MS,
} from '../components/cap-table-table-animations';
import { CustomizeViewResult, CustomView } from './details';
import { DetailsOrderingTab } from './details-ordering-tab';
import { DetailsViewColumnsTab, ViewColumnsSettings } from './details-view-columns-tab';

/** The two tabs of the modal: the long-standing view/columns controls, and the new ordering ones. */
export enum CustomizeViewTab {
  ORDERING = 'ordering',
  VIEW_AND_COLUMNS = 'view-and-columns',
}

const CUSTOMIZE_VIEW_TAB_TITLES: [CustomizeViewTab, StringKey][] = [
  [CustomizeViewTab.VIEW_AND_COLUMNS, 'CAP_TABLE_VIEW_AND_COLUMNS'],
  [CustomizeViewTab.ORDERING, 'CAP_TABLE_ORDERING'],
];

type DetailsCustomizeViewProps = {
  availableTypes: StakeholderType[];
  /** Whether the view on screen is the saved default — seeds the Set as Default View checkbox. */
  isDefaultView: boolean;
  onCancel: () => void;
  onSuccess: (settings: CustomizeViewResult) => void;
  ordering: CapTableOrdering;
  selectedForTab: DetailsSortVariant;
  viewSettings: CustomView;
};

export type CustomViewSettings = Record<DetailsSortVariant, ViewColumnsSettings>;

const defaultShareClassColumns = detailsColumns[DetailsSortVariant.BY_SHARE_CLASS].reduce(
  (acc, curr) => ({ ...acc, [curr]: false }),
  {} as Record<ColumnVariant, boolean>,
);

const defaultStakeholderColumns = detailsColumns[DetailsSortVariant.BY_STAKEHOLDER].reduce(
  (acc, curr) => ({ ...acc, [curr]: false }),
  {} as Record<ColumnVariant, boolean>,
);

const toSelectedColumns = (
  settings: CustomViewSettings,
  variant: DetailsSortVariant,
): CustomView[DetailsSortVariant] => ({
  columns: Object.entries(settings[variant].columns)
    .filter(([_, state]) => state)
    .map(([field]) => field as ColumnVariant),
  viewBy: settings[variant].viewBy,
});

const isSameFlags = <K extends string>(a: Record<K, boolean>, b: Record<K, boolean>): boolean =>
  (Object.keys({ ...a, ...b }) as K[]).every((key) => Boolean(a[key]) === Boolean(b[key]));

const isSameSettings = (a: CustomViewSettings, b: CustomViewSettings): boolean =>
  Object.values(DetailsSortVariant).every(
    (variant) =>
      isSameFlags(a[variant].viewBy, b[variant].viewBy) &&
      isSameFlags(a[variant].columns, b[variant].columns),
  );

const isSameOrdering = (a: CapTableOrdering, b: CapTableOrdering): boolean =>
  a.mode === b.mode &&
  a.withinType === b.withinType &&
  a.typeOrder.length === b.typeOrder.length &&
  a.typeOrder.every((type, index) => type === b.typeOrder[index]);

export const DetailsCustomizeView: FC<DetailsCustomizeViewProps> = ({
  availableTypes,
  isDefaultView,
  onCancel,
  onSuccess,
  ordering,
  viewSettings,
  selectedForTab,
}) => {
  const [selectedModalTab, setSelectedModalTab] = useState(CustomizeViewTab.VIEW_AND_COLUMNS);
  const [selectedCustomViewTab, setSelectedCustomViewTab] =
    useState<DetailsSortVariant>(selectedForTab);

  const modalRef = useRef<HTMLDivElement>(null);
  const heightBeforeSwitch = useRef<null | number>(null);

  /**
   * Measured here rather than inside the state updater: React may re-run an updater, and a second
   * run reads the height of the content that already replaced it — leaving nothing to animate from.
   *
   * Both switches in this modal resize it by a hundred pixels or more — the tabs, and the By
   * toggle, which drops the whole View By section and swaps the column list — so both pin through
   * here and animate identically.
   */
  const pinCurrentHeight = () => {
    heightBeforeSwitch.current = modalRef.current?.getBoundingClientRect().height ?? null;
  };

  const handleSelectTab = (tab: CustomizeViewTab) => {
    if (tab === selectedModalTab) return;

    pinCurrentHeight();
    setSelectedModalTab(tab);
  };

  const handleSelectVariant = (variant: DetailsSortVariant) => {
    if (variant === selectedCustomViewTab) return;

    pinCurrentHeight();
    setSelectedCustomViewTab(variant);
  };

  /**
   * A switch can change the modal's height by hundreds of pixels, which resized it — and jumped
   * the footer — in a single frame.
   *
   * The height belongs on the modal, which is the box that actually changes size: the panel below
   * is `overflow-y-auto`, so pinning a height there only makes it scroll while the modal carries
   * on sizing itself from its content.
   *
   * Driven through the DOM rather than an `animate` prop because React coalesces this effect's
   * update into the commit that swapped the content, leaving a declarative animation with only the
   * final height and nothing to move from; the forced reflow is what commits the starting height.
   * At rest no inline height is left behind, so the layout owns it and stays correct even if the
   * transition never runs — which the timeout also guarantees.
   */
  useLayoutEffect(() => {
    const modal = modalRef.current;
    const from = heightBeforeSwitch.current;
    heightBeforeSwitch.current = null;

    if (!modal || from === null) return;

    const to = modal.getBoundingClientRect().height;
    if (from === to || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const release = () => {
      modal.removeEventListener('transitionend', handleTransitionEnd);
      modal.style.transition = '';
      modal.style.height = '';
    };

    /**
     * `transitionend` bubbles, and the modal is full of things that transition — the tab labels'
     * colour, the Ordering cards, the switches. Listening for any of them ended the height
     * animation as soon as the first one finished, which is why it read as a snap.
     */
    function handleTransitionEnd(event: TransitionEvent) {
      if (event.target !== modal || event.propertyName !== 'height') return;

      release();
    }

    modal.style.transition = 'none';
    modal.style.height = `${from}px`;
    void modal.offsetHeight;

    modal.style.transition = `height ${CAP_TABLE_ROW_EXPAND_MS}ms ${CAP_TABLE_ROW_EXPAND_EASING}`;
    modal.style.height = `${to}px`;
    modal.addEventListener('transitionend', handleTransitionEnd);

    // Nothing may leave the modal pinned to a stale height: a backgrounded tab never fires
    // `transitionend`, and neither does a switch interrupted by another one.
    const timer = window.setTimeout(release, CAP_TABLE_ROW_EXPAND_MS + 100);

    return () => {
      window.clearTimeout(timer);
      release();
    };
  }, [selectedCustomViewTab, selectedModalTab]);

  const selectedShareClassColumns = useMemo(
    () =>
      viewSettings[DetailsSortVariant.BY_SHARE_CLASS].columns.reduce(
        (acc, curr) => ({ ...acc, [curr]: true }),
        {},
      ),
    [viewSettings],
  );

  const selectedStakeholdersColumns = useMemo(
    () =>
      viewSettings[DetailsSortVariant.BY_STAKEHOLDER].columns.reduce(
        (acc, curr) => ({ ...acc, [curr]: true }),
        {},
      ),
    [viewSettings],
  );

  // Kept apart from the editable copy so Save can tell whether anything was actually changed.
  const [initialSettings] = useState<CustomViewSettings>(() => ({
    [DetailsSortVariant.BY_SHARE_CLASS]: {
      viewBy: viewSettings[DetailsSortVariant.BY_SHARE_CLASS].viewBy,
      columns: { ...defaultShareClassColumns, ...selectedShareClassColumns },
    },
    [DetailsSortVariant.BY_STAKEHOLDER]: {
      viewBy: viewSettings[DetailsSortVariant.BY_STAKEHOLDER].viewBy,
      columns: { ...defaultStakeholderColumns, ...selectedStakeholdersColumns },
    },
  }));

  const [customSettings, setCustomSettings] = useState<CustomViewSettings>(initialSettings);

  const [customOrdering, setCustomOrdering] = useState<CapTableOrdering>(ordering);

  // The checkbox governs the whole modal, not one tab: both tabs are only committed on Save. It
  // starts from the parent's last Save because the modal remounts on every open — a hardcoded
  // `true` re-checked it after a session-only Save.
  const [setAsDefaultView, setAsDefaultViewState] = useState(isDefaultView);

  const customSettingSelectedTab = customSettings[selectedCustomViewTab];

  const handleChangeViewBy = (field: ViewBy, state: boolean) =>
    setCustomSettings((prev) => ({
      ...prev,
      [selectedCustomViewTab]: {
        ...prev[selectedCustomViewTab],
        viewBy: { ...prev[selectedCustomViewTab].viewBy, [field]: state },
      },
    }));

  const handleChangeColumnView = (field: ColumnVariant, state: boolean) =>
    setCustomSettings((prev) => ({
      ...prev,
      [selectedCustomViewTab]: {
        ...prev[selectedCustomViewTab],
        columns: { ...prev[selectedCustomViewTab].columns, [field]: state },
      },
    }));

  const handleSuccess = useCallback(() => {
    const view: CustomView = {
      [DetailsSortVariant.BY_SHARE_CLASS]: toSelectedColumns(
        customSettings,
        DetailsSortVariant.BY_SHARE_CLASS,
      ),
      [DetailsSortVariant.BY_STAKEHOLDER]: toSelectedColumns(
        customSettings,
        DetailsSortVariant.BY_STAKEHOLDER,
      ),
    };

    // Persistence happens here and nowhere else, so Cancel really does discard both tabs. An
    // unchecked box is a session-only change: it must leave the saved default untouched so a reload
    // reverts to it — clearing the store here made a reload fall back to the built-in default.
    if (setAsDefaultView) {
      capTableStore.set({
        [DetailsSortVariant.BY_SHARE_CLASS]: view[DetailsSortVariant.BY_SHARE_CLASS],
        [DetailsSortVariant.BY_STAKEHOLDER]: view[DetailsSortVariant.BY_STAKEHOLDER],
        ordering: customOrdering,
      });
    }

    onSuccess({ isDefaultView: setAsDefaultView, ordering: customOrdering, view });
  }, [customOrdering, customSettings, onSuccess, setAsDefaultView]);

  // Save is shared by both tabs, so it must not depend on which one is on screen: an unusable
  // View and columns selection has to block Save while the Ordering tab is showing too.
  const isValid = Object.values(DetailsSortVariant).every(
    (variant) => customSettings[variant].viewBy.stakeholders || customSettings[variant].viewBy.type,
  );

  // Compared by value, so toggling something and back leaves Save disabled again. The Set as
  // Default View checkbox counts: checking it alone promotes the current view to the saved default.
  const isDirty =
    setAsDefaultView !== isDefaultView ||
    !isSameOrdering(customOrdering, ordering) ||
    !isSameSettings(customSettings, initialSettings);

  const canSave = isValid && isDirty;

  return (
    <div
      className="flex max-h-[90vh] w-[432px] flex-col gap-6 rounded bg-white p-6 shadow-3xl"
      ref={modalRef}
    >
      <div className="flex shrink-0 flex-col">
        <div className="flex items-center border-b border-b-gray-100 pb-4">
          <span className="text-xl font-semibold text-gray-700">
            <AppFormattedMessage id="CUSTOMIZE_VIEW" />
          </span>
        </div>

        <div className="flex gap-4 border-b border-b-gray-200 pt-4">
          {CUSTOMIZE_VIEW_TAB_TITLES.map(([tabKey, tabTitle]) => (
            <button
              className={twMerge(
                'relative px-1 pb-4 text-sm font-[450] text-gray-400 transition-colors',
                selectedModalTab === tabKey && 'font-[550] text-brand-700',
              )}
              key={tabKey}
              onClick={() => handleSelectTab(tabKey)}
              type="button"
            >
              <span className="text-nowrap">
                <AppFormattedMessage id={tabTitle} />
              </span>
              {/* Deliberately not animated: a sliding underline lags behind the tab the viewer
                  just clicked, which reads as slow. It belongs to the active tab immediately. */}
              {selectedModalTab === tabKey && (
                <span className="absolute inset-x-0 -bottom-px h-0.5 bg-brand-700" />
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Both tabs stay mounted in state — only the rendered panel changes — so moving between
          them never drops an edit the viewer has not saved yet.

          While a switch is animating the panel owns its height and clips; at rest it goes back to
          being a flex child so the Ordering tab's own expand/collapse resizes it natively rather
          than racing this animation. */}
      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
        <div className="shrink-0">
          {/* No `AnimatePresence`: `mode="wait"` played the outgoing panel out first, so the modal
              collapsed to an empty box and grew again on every tab switch. Re-keying the panel
              lets the new tab fade straight in over a single height change.

              `translate={false}`: the default 8px slide starts the incoming panel below its
              resting place, which overflows the scroll container long enough to flash a
              scrollbar. */}
          <CrossfadePanel
            className="flex flex-col gap-4"
            key={selectedModalTab}
            panelKey={selectedModalTab}
            translate={false}
          >
            {selectedModalTab === CustomizeViewTab.VIEW_AND_COLUMNS ? (
              <DetailsViewColumnsTab
                onChangeColumn={handleChangeColumnView}
                onChangeViewBy={handleChangeViewBy}
                onSelectVariant={handleSelectVariant}
                selectedVariant={selectedCustomViewTab}
                settings={customSettingSelectedTab}
              />
            ) : (
              <DetailsOrderingTab
                availableTypes={availableTypes}
                onChange={setCustomOrdering}
                ordering={customOrdering}
              />
            )}
          </CrossfadePanel>
        </div>
      </div>

      <div className="flex shrink-0 items-center justify-between">
        <div className="flex gap-2">
          <Checkbox checked={setAsDefaultView} onChange={setAsDefaultViewState} />
          <span className="text-xs font-[450] text-gray-700">
            <AppFormattedMessage id="SET_AS_DEFAULT_VIEW" />
          </span>
        </div>
        <div className="flex h-11 gap-3">
          <Button
            className="h-full w-fit rounded border border-gray-300 px-6 text-sm font-[450] text-gray-700"
            onClick={onCancel}
            styleType="NONE"
          >
            <AppFormattedMessage id="CANCEL" />
          </Button>
          <Button
            className="h-full w-fit px-6 text-sm font-[550] text-white"
            disabled={!canSave}
            onClick={handleSuccess}
          >
            <AppFormattedMessage id="SAVE" />
          </Button>
        </div>
      </div>
    </div>
  );
};
